/**
 * Big Bear's caseload: the five villains, shared by the two pages that need
 * to agree about them.
 *
 * The home page renders the case files and `/features` credits each group with
 * the ones it closes. Keeping the list here means the credit is *derived* from
 * the same `anchor` the card links out on, so a villain can never be pointed at
 * a feature group on one page and credited to a different one on the other —
 * which is the whole reason the anchors exist.
 */

export interface Villain {
  id: string;
  name: string;
  threat: string;
  image: string;
  alt: string;
  brief: string;
  win: string;
  /** The feature group that actually answers this threat. */
  anchor: string;
}

/** Every villain is one real failure mode, and every `anchor` points at the
 * feature group that actually handles it — the joke is never allowed to stand
 * in for the claim. */
export const VILLAINS: Villain[] = [
  {
    id: "wraiths",
    name: "The Ransom Wraiths",
    threat: "Ransomware",
    image: "/bear/villain-wraiths.webp",
    alt: "Padlock-headed figures in a flooded server hall, dragging caged, glowing photographs behind them on chains.",
    brief: "Padlock-headed extortionists who chain up your files and wait for tribute.",
    win: "Versioned, encrypted snapshots on storage they can't reach. Restore to the minute before the locks went on — there's nothing to negotiate.",
    anchor: "/features#security",
  },
  {
    id: "titan",
    name: "The Rot Titan",
    threat: "Bit rot & hardware failure",
    image: "/bear/villain-titan.webp",
    alt: "A colossus built from dead hard drives and cabling, wading through fog above a lit city.",
    brief: "A colossus of dead drives and rusted platters. Every step kills another disk.",
    win: "Optional Reed–Solomon parity heals partial corruption, and verify re-reads and re-authenticates every byte — not just the index.",
    anchor: "/features#integrity",
  },
  {
    id: "glitch",
    name: "The Glitch",
    threat: "Silent corruption",
    image: "/bear/villain-glitch.webp",
    alt: "Red-eyed humanoid figures half-dissolved into streaks of corrupted data, emerging from steam in a rain-soaked alley.",
    brief: "Specters that flip one bit and vanish. You find out years later, when it's the only copy.",
    win: "Authenticated encryption on every blob and keyed chunk IDs: tampered or truncated data is rejected loudly, never restored quietly wrong.",
    anchor: "/features#integrity",
  },
  {
    id: "phisher",
    name: "The Phisher",
    threat: "Bad actors & human error",
    image: "/bear/villain-phisher.webp",
    alt: "A man in a suit and a smiling mask standing in the rain on an empty street, one hand held out.",
    brief: "Always smiling, always helpful, always after your only copy.",
    win: "Your passphrase wraps a random master secret and never leaves your machine — and point-in-time snapshots undo whatever he talked you into.",
    anchor: "/features#security",
  },
  {
    id: "swarm",
    name: "The Swarm",
    threat: "Accidents",
    image: "/bear/villain-swarm.webp",
    alt: "A grizzly bear charging head-on through a cloud of shattering insect-sized drones in a server corridor.",
    brief: "A thousand small disasters: the spilled coffee, the fat-fingered delete, the sync to nowhere.",
    win: "Incremental backups so cheap you can run them constantly. Every version kept, deduplicated, none of them precious.",
    anchor: "/features#efficiency",
  },
];

/** The id of a case file's card on the home page. Namespaced, because these
 * ids share a document with the feature-group ids and with whatever the spec's
 * slugger emits. */
export function villainAnchor(villainId: string): string {
  return `villain-${villainId}`;
}

/** The villains a feature group closes the case on, in caseload order. Empty
 * for the groups that answer no single villain — a credit is only worth
 * printing where there is one. */
export function defeatedBy(groupId: string): Villain[] {
  return VILLAINS.filter((villain) => villain.anchor === `/features#${groupId}`);
}
